import { useTranslation } from "react-i18next"
import { type UseMutationResult, useMutation } from "react-query"

import { NotificationUtils } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"

import { KonanAPI } from "../../../services/KonanAPI"
import { queryClient } from "../../../store/QueryClientStore"
import type { ActualDecisionCreateRequest } from "../../../types/generated/api/ActualDecisionCreateRequest"
import type { ActualDecisionCreateResponse } from "../../../types/generated/api/ActualDecisionCreateResponse"
import { type StandardErrorType } from "../../../utils/ErrorHandling"

/**
 * Custom hook for creating actual decision mutations.
 * Handles the creation of actual decisions for predictions with success notifications
 * and automatic query invalidation.
 *
 * @returns Mutation result object with loading state, error handling, and success callbacks
 */
export function useCreateActualDecisionMutation(): UseMutationResult<
  AxiosResponse<ActualDecisionCreateResponse>,
  AxiosError<StandardErrorType>,
  ActualDecisionCreateRequest
> {
  const { t } = useTranslation("decisions", {
    keyPrefix: "projectDecisions",
  })

  return useMutation<
    AxiosResponse<ActualDecisionCreateResponse>,
    AxiosError<StandardErrorType>,
    ActualDecisionCreateRequest
  >((payload) => KonanAPI.createActualDecisions(payload), {
    onSuccess() {
      NotificationUtils.toast(t("notifications.actualDecisionUpdated"), {
        severity: "success",
      })
      queryClient.invalidateQueries(["project-predictions"])
    },
  })
}
